/**
 * Diagnostics page renderer (web-client.md §8). Pure view: maps the {@link CheckResult}s + status
 * lines into terminal lines with a pass/warn/fail glyph and colour class. It reads results; it runs
 * nothing (the {@link DiagnosticsPage} controller owns the probes and re-render loop).
 */
import type { CheckResult, CheckStatus } from '../../core/index.ts';
import type { TermLine } from '../term/index.ts';
import { t } from '../i18n.ts';

const GLYPH: Record<CheckStatus, string> = {
  pass: '[ OK ]',
  warn: '[WARN]',
  fail: '[FAIL]',
  skip: '[ -- ]',
};

const CLS: Record<CheckStatus, string> = {
  pass: 'title',
  warn: 'sys',
  fail: 'err',
  skip: 'dim',
};

export interface DiagnosticsViewModel {
  serverUrl: string;
  running: boolean;
  results: CheckResult[];
  log: string[];
}

export function renderDiagnostics(vm: DiagnosticsViewModel): TermLine[] {
  const lines: TermLine[] = [];
  lines.push({ text: t('diag.title'), cls: 'title' });
  lines.push({ text: t('diag.server', { url: vm.serverUrl }), cls: 'dim' });
  lines.push({ text: vm.running ? 'running checks…' : 'checks complete.', cls: 'dim' });
  lines.push({ text: '' });

  if (vm.results.length === 0 && !vm.running) {
    lines.push({ text: t('diag.typeRun'), cls: 'sys' });
  }
  for (const r of vm.results) {
    lines.push({ text: `${GLYPH[r.status]} ${r.label}`, cls: CLS[r.status] });
    lines.push({ text: `        ${r.detail}`, cls: 'dim' });
  }

  const { pass, warn, fail } = tally(vm.results);
  if (vm.results.length) {
    lines.push({ text: '' });
    lines.push({
      text: t('diag.summary', { pass, warn, fail }),
      cls: fail ? 'err' : warn ? 'sys' : 'title',
    });
  }

  lines.push({ text: '' });
  lines.push({
    text: t('diag.commands'),
    cls: 'dim',
  });
  if (vm.log.length) {
    lines.push({ text: '' });
    for (const entry of vm.log.slice(-5)) lines.push({ text: entry, cls: 'log' });
  }
  return lines;
}

function tally(results: CheckResult[]): { pass: number; warn: number; fail: number } {
  let pass = 0;
  let warn = 0;
  let fail = 0;
  for (const r of results) {
    if (r.status === 'pass') pass++;
    else if (r.status === 'warn') warn++;
    else if (r.status === 'fail') fail++;
  }
  return { pass, warn, fail };
}
